import React, { useEffect, useMemo, useRef, useState } from 'react';
import { kpiAPI, usersAPI } from '../services/api';
import InfoTooltip from '../components/InfoTooltip';

function Pill({ text, color = 'gray' }) {
  const classes = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`;
  return <span className={classes}>{text}</span>;
}

export default function KpiAdmin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [cycleId, setCycleId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [cycles, setCycles] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [scope, setScope] = useState('users');
  const [userQuery, setUserQuery] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // array of {id, officialEmail, firstName, lastName}
  const [departmentName, setDepartmentName] = useState('');
  const [managerQuery, setManagerQuery] = useState('');
  const [managerOptions, setManagerOptions] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentQuery, setDepartmentQuery] = useState('');
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const debounceRef = useRef();

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await kpiAPI.listSubmissionsAdmin({ cycleId, status, q });
      setSubmissions(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); // initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cyclesResponse, departmentsResponse] = await Promise.all([
          kpiAPI.listCycles(),
          usersAPI.getDepartmentsWithCount()
        ]);
        
        setCycles(cyclesResponse.data);
        if (cyclesResponse.data?.length && !cycleId) setCycleId(cyclesResponse.data[0].id);
        
        // Extract department names from the response
        const departmentNames = departmentsResponse.data?.departments?.map(dept => dept.name) || [];
        console.log('KPI Admin loaded departments:', departmentNames);
        setDepartments(departmentNames);
      } catch (e) {
        // ignore for now
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search users for selectize (both specific users and manager)
  useEffect(() => {
    const run = async () => {
      try {
        const term = scope === 'team' ? managerQuery : userQuery;
        if (!term || term.length < 2) {
          if (scope === 'team') setManagerOptions([]); else setUserOptions([]);
          return;
        }
        const { data } = await usersAPI.getUsers({ search: term, limit: 10, page: 1 });
        const opts = (data.users || []).map(u => ({ id: u.id, email: u.officialEmail, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() }));
        if (scope === 'team') setManagerOptions(opts); else setUserOptions(opts);
      } catch (_) {}
    };
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(run, 250);
    return () => clearTimeout(debounceRef.current);
  }, [userQuery, managerQuery, scope]);

  // Search departments for selectize
  useEffect(() => {
    const run = async () => {
      try {
        if (!departmentQuery || departmentQuery.length < 1) {
          setDepartmentOptions([]);
          return;
        }
        // Filter departments based on query
        const filtered = departments.filter(dept => 
          dept.toLowerCase().includes(departmentQuery.toLowerCase())
        );
        setDepartmentOptions(filtered);
      } catch (_) {}
    };
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(run, 150);
    return () => clearTimeout(debounceRef.current);
  }, [departmentQuery, departments]);

  const statusColor = (s) => ({
    draft: 'gray',
    submitted: 'blue',
    needs_changes: 'yellow',
    finalized: 'green',
  }[s] || 'gray');

  const totalWeight = (items) => items?.reduce((sum, it) => sum + (Number(it.weight) || 0), 0) || 0;

  // Template editor state
  const [templateItems, setTemplateItems] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState('');

  const loadTemplate = async (cid) => {
    if (!cid) return;
    setTplLoading(true); setTplError('');
    try {
      const { data } = await kpiAPI.getTemplateItems(cid);
      setTemplateItems(data || []);
    } catch (e) {
      setTplError(e.response?.data?.error || e.message);
    } finally { setTplLoading(false); }
  };

  useEffect(()=>{ if (cycleId) loadTemplate(cycleId); }, [cycleId]);

  const addTplItem = () => setTemplateItems([ ...templateItems, { title:'', description:'', weight: 20, targetValue:'', required: true } ]);
  const removeTplItem = (idx) => setTemplateItems(templateItems.filter((_,i)=>i!==idx));
  const saveTpl = async () => {
    try {
      const total = templateItems.reduce((s,i)=> s + Number(i.weight||0), 0);
      if (total !== 100) throw new Error('Weights must sum to 100');
      await kpiAPI.setTemplateItems(cycleId, templateItems);
      await loadTemplate(cycleId);
    } catch(e) { setTplError(e.response?.data?.error || e.message); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
        <h1 className="text-2xl font-semibold">KPI Admin</h1>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <select value={cycleId} onChange={e=>setCycleId(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="">All Cycles</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name/email" className="border rounded px-2 py-1 text-sm" />
          <select value={status} onChange={e=>setStatus(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="needs_changes">Needs Changes</option>
            <option value="finalized">Finalized</option>
          </select>
          <button onClick={fetchData} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded">Refresh</button>
        </div>
      </div>
      <div className="mb-4 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded p-3">
        <div className="flex items-start gap-2">
          <div className="mt-[2px]">ℹ️</div>
          <div>
            <div className="font-medium text-blue-800">How it works</div>
            <div>
              Publish a cycle → Admin defines default items and weights (sum to 100%) → Employees can add any number of their own items (weightless) and submit self-assessment → Managers review and finalize. Admins can unlock or set status if needed.
            </div>
          </div>
        </div>
      </div>

      {/* Publish panel */}
      <div className="mb-6 bg-white border rounded p-4">
        <div className="flex flex-col sm:flex-row flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500">Cycle</label>
            <select value={cycleId} onChange={e=>setCycleId(e.target.value)} className="border rounded px-2 py-1 text-sm min-w-[12rem]">
              {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500">Scope <InfoTooltip text="Choose who gets a submission created for this cycle" /></label>
            <select value={scope} onChange={e=>setScope(e.target.value)} className="border rounded px-2 py-1 text-sm">
              <option value="users">Specific Users</option>
              <option value="department">Department</option>
              <option value="team">Manager's Team</option>
            </select>
          </div>
          {scope === 'users' && (
            <div className="flex-1 min-w-[14rem]">
              <label className="block text-xs text-gray-500">Select Users <InfoTooltip text="Search by email/name and add one or more users" /></label>
              <div className="border rounded p-2">
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedUsers.map(u => (
                    <span key={u.id} className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs">
                      {u.email}
                      <button className="ml-1 text-indigo-500" onClick={()=>setSelectedUsers(selectedUsers.filter(x=>x.id!==u.id))}>×</button>
                    </span>
                  ))}
                </div>
                <input
                  value={userQuery}
                  onChange={e=>setUserQuery(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full border rounded px-2 py-1 text-sm"
                />
                {userOptions.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-auto border rounded">
                    {userOptions.map(o => (
                      <div key={o.id} className="px-2 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                        onClick={()=>{ if(!selectedUsers.find(x=>x.id===o.id)) setSelectedUsers([...selectedUsers, { id:o.id, email:o.email, name:o.name }]); setUserQuery(''); setUserOptions([]); }}>
                        {o.email} <span className="text-gray-500">({o.name})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {scope === 'department' && (
            <div className="min-w-[14rem]">
              <label className="block text-xs text-gray-500">Department Name <InfoTooltip text="Search and select a department; all users in it will get submissions" /></label>
              <div className="border rounded p-2">
                {selectedDepartment && (
                  <div className="mb-2 text-sm">
                    Selected: <span className="font-medium">{selectedDepartment}</span>
                    <button className="ml-2 text-indigo-600" onClick={()=>{setSelectedDepartment(null); setDepartmentName(''); setDepartmentQuery(''); setDepartmentOptions([]);}}>Change</button>
                  </div>
                )}
                {!selectedDepartment && (
                  <>
                    <input
                      value={departmentQuery}
                      onChange={e=>setDepartmentQuery(e.target.value)}
                      placeholder="Type to search departments..."
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                    {departmentOptions.length > 0 && (
                      <div className="mt-1 max-h-40 overflow-auto border rounded">
                        {departmentOptions.map(dept => (
                          <div key={dept} className="px-2 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                            onClick={()=>{ 
                              setSelectedDepartment(dept); 
                              setDepartmentName(dept);
                              setDepartmentQuery(''); 
                              setDepartmentOptions([]); 
                            }}>
                            {dept}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          {scope === 'team' && (
            <div className="min-w-[14rem]">
              <label className="block text-xs text-gray-500">Manager <InfoTooltip text="Select a manager; their team's users will get submissions" /></label>
              <div className="border rounded p-2">
                {selectedManager && (
                  <div className="mb-2 text-sm">
                    Selected: <span className="font-medium">{selectedManager.email}</span>
                    <button className="ml-2 text-indigo-600" onClick={()=>setSelectedManager(null)}>Change</button>
                  </div>
                )}
                {!selectedManager && (
                  <>
                    <input
                      value={managerQuery}
                      onChange={e=>setManagerQuery(e.target.value)}
                      placeholder="Type to search..."
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                    {managerOptions.length > 0 && (
                      <div className="mt-1 max-h-40 overflow-auto border rounded">
                        {managerOptions.map(o => (
                          <div key={o.id} className="px-2 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                            onClick={()=>{ setSelectedManager(o); setManagerQuery(''); setManagerOptions([]); }}>
                            {o.email} <span className="text-gray-500">({o.name})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          <button
            onClick={async ()=>{
              setPublishing(true); setError('');
              try {
                const payload = { cycleId, scope };
                if (scope === 'users') payload.userIds = selectedUsers.map(u=>u.id);
                if (scope === 'department') payload.departmentName = selectedDepartment || departmentName;
                if (scope === 'team') payload.managerId = selectedManager?.id;
                await kpiAPI.publishAdmin(payload);
                await fetchData();
              } catch(e){ setError(e.response?.data?.error || e.message); }
              finally { setPublishing(false); }
            }}
            disabled={!cycleId || publishing}
            className={`px-3 py-2 text-sm rounded text-white ${publishing ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
          >{publishing ? 'Publishing...' : 'Publish'}</button>
          <div className="text-xs text-gray-500 ml-2">
            Creates a submission for the selected audience. Existing submissions are kept.
          </div>
        </div>
      </div>

      {/* Default Items (Template) */}
      <div className="mb-6 bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-700">Default Items (Template)</div>
          <InfoTooltip text="These items prefill employee submissions after publish. Employees cannot edit/remove items marked Required. Any number of items is allowed; weights must total 100%." />
        </div>
        <div className="text-xs text-gray-600 mb-3">
          What to enter:
          <span className="ml-1 font-medium">Title</span> (the KPI name),
          <span className="ml-1 font-medium">Description</span> (how it will be achieved/measured),
          <span className="ml-1 font-medium">Weight %</span> (importance; all items must add to 100),
          <span className="ml-1 font-medium">Target</span> (quantitative goal like “Ship X by Y”).
        </div>
        {tplError && <div className="mb-2 text-red-600 text-xs">{tplError}</div>}
        {tplLoading ? <div>Loading template...</div> : (
          <>
            {/* Column labels for clarity (shown on larger screens) */}
            <div className="hidden md:grid md:grid-cols-6 gap-2 text-[11px] text-gray-500 mb-2">
              <div className="md:col-span-2">Title</div>
              <div className="md:col-span-2">Description</div>
              <div>Weight %</div>
              <div>Target</div>
            </div>
            <div className="space-y-3">
              {templateItems.map((it, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-start">
                  <input className="border rounded px-2 py-2 md:col-span-2" placeholder="Title (e.g., Deliver Feature X)" value={it.title}
                    onChange={e=>{ const arr=[...templateItems]; arr[idx].title=e.target.value; setTemplateItems(arr); }} />
                  <input className="border rounded px-2 py-2 md:col-span-2" placeholder="Description (key activities / measurement)" value={it.description||''}
                    onChange={e=>{ const arr=[...templateItems]; arr[idx].description=e.target.value; setTemplateItems(arr); }} />
                  <input className="border rounded px-2 py-2" type="number" min={1} max={100} placeholder="Weight %" value={it.weight}
                    onChange={e=>{ const arr=[...templateItems]; arr[idx].weight=e.target.value; setTemplateItems(arr); }} />
                  <input className="border rounded px-2 py-2" placeholder="Target (e.g., 99% uptime, 20% growth)" value={it.targetValue}
                    onChange={e=>{ const arr=[...templateItems]; arr[idx].targetValue=e.target.value; setTemplateItems(arr); }} />
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={!!it.required} onChange={e=>{ const arr=[...templateItems]; arr[idx].required=e.target.checked; setTemplateItems(arr); }} /> Required
                  </label>
                  <div className="flex items-center gap-2">
                    <button className="px-2 py-1 rounded border" onClick={()=>removeTplItem(idx)}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button className="px-3 py-2 rounded border" onClick={addTplItem}>Add Item</button>
              <button className="px-3 py-2 bg-indigo-600 text-white rounded" onClick={saveTpl} disabled={!cycleId}>Save Template</button>
              <div className="text-xs text-gray-500">Total weight: {totalWeight(templateItems)}%</div>
            </div>
          </>
        )}
      </div>

      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
        <div className="overflow-x-auto bg-white shadow rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weights</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map(s => (
                <tr key={s.id}>
                  <td className="px-4 py-2 whitespace-nowrap">{s.employee?.firstName} {s.employee?.lastName}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{s.employee?.officialEmail}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{s.items?.length || 0}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">{totalWeight(s.items)}%</td>
                  <td className="px-4 py-2 whitespace-nowrap"><Pill text={s.status} color={statusColor(s.status)} /></td>
                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                    <button
                      title={s.status === 'finalized' ? 'Unlock to allow manager edits' : 'Finalize to lock this submission'}
                      onClick={async ()=>{ 
                        try { 
                          await kpiAPI.updateSubmissionStatusAdmin(s.id, { status: s.status === 'finalized' ? 'submitted' : 'finalized' }); 
                          // Fetch the updated submission with includes to avoid any blank fields
                          const res = await kpiAPI.getSubmissionAdmin(s.id);
                          const updated = res?.data;
                          if (updated) {
                            setSubmissions(prev => prev.map(x => x.id === s.id ? updated : x));
                          } else {
                            await fetchData();
                          }
                        } catch(e){ 
                          setError(e.response?.data?.error || e.message); 
                        }
                      }}
                      className="px-3 py-1 rounded border text-gray-700 hover:bg-gray-50"
                    >{s.status === 'finalized' ? 'Unlock' : 'Finalize'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-gray-200" /> draft</div>
            <div className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-200" /> submitted</div>
            <div className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-yellow-200" /> needs_changes</div>
            <div className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-green-200" /> finalized</div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}


